import { useMemo } from "react";
import { Box, Cyl, Ball } from "./Box";
import { getTexture } from "./textures";
import { Monstera, SnakePlant, Pothos, Succulent, Sapling } from "./Plants";
import { ArtFrame, Totoro, SootSprites, Kodama, StrawHat, Gojo, FrierenStaff, Mimic, Cassettes, OilDerrick, BearCarving } from "./Refs";

const WOOD = "#8A5A35";
const WOOD_DARK = "#5C3A21";
const CREAM = "#EFE3CF";
const SAGE = "#6E8B74";
const TERRA = "#C85A32";

const Shell = () => (
  <group>
    <Box p={[0, -0.2, 0]} s={[8.4, 0.4, 8.4]} c="#8A5532" r={0.55} t="planks" tr={[1.4, 1]} />
    <Box p={[0, 2.5, -4.1]} s={[8.4, 5, 0.2]} c="#5C7863" r={0.95} t="plaster" tr={[2.5, 1.5]} />
    <Box p={[-4.1, 2.5, 0]} s={[0.2, 5, 8.4]} c="#B8664A" r={0.95} t="plaster" tr={[2.5, 1.5]} />
    <Box p={[0, 0.09, -3.98]} s={[8, 0.18, 0.05]} c={CREAM} r={0.5} t="plastic" />
    <Box p={[-3.98, 0.09, 0]} s={[0.05, 0.18, 8]} c={CREAM} r={0.5} t="plastic" />
    <Box p={[0, 0.6, -3.995]} s={[8, 1.2, 0.02]} c="#4E6956" r={0.7} t="plaster" tr={[3, 0.5]} />
    {Array.from({ length: 7 }, (_, i) => (
      <group key={i} position={[-3.45 + i * 1.15, 0.65, -3.98]}>
        <Box p={[0, 0.34, 0]} s={[0.9, 0.035, 0.02]} c="#455E4C" r={0.6} />
        <Box p={[0, -0.34, 0]} s={[0.9, 0.035, 0.02]} c="#455E4C" r={0.6} />
        <Box p={[-0.43, 0, 0]} s={[0.035, 0.7, 0.02]} c="#455E4C" r={0.6} />
        <Box p={[0.43, 0, 0]} s={[0.035, 0.7, 0.02]} c="#455E4C" r={0.6} />
      </group>
    ))}
    <Box p={[0, 1.22, -3.97]} s={[8, 0.05, 0.06]} c={CREAM} r={0.5} t="plastic" />
  </group>
);

const Window = () => (
  <group position={[-3.97, 2.75, -0.6]}>
    <Box p={[0, 0, 0]} s={[0.1, 1.95, 2.3]} c={CREAM} r={0.5} t="wood" />
    <Box p={[0.03, 0, 0]} s={[0.08, 1.75, 2.1]} c="#FFB27A" e="#FF8A3D" ei={2.2} shadow={false} />
    <Box p={[0.08, 0, 0]} s={[0.04, 1.75, 0.06]} c={CREAM} />
    <Box p={[0.08, 0.15, 0]} s={[0.04, 0.06, 2.1]} c={CREAM} />
    <Box p={[0.2, -1.0, 0]} s={[0.35, 0.06, 2.5]} c={CREAM} />
    {[1.35, -1.35].map((z) => (
      <group key={z} position={[0.12, 0.1, z]}>
        {[-0.13, 0, 0.13].map((dz, i) => (
          <Box key={dz} p={[i === 1 ? 0.03 : 0, 0, dz]} s={[0.07, 2.3, 0.16]} c="#E3CFB0" r={1} t="fabric" tr={[1, 4]} round={0.03} />
        ))}
      </group>
    ))}
    <Box p={[0.14, 1.27, 0]} s={[0.05, 0.05, 3.3]} c={WOOD_DARK} t="wood" />
    {[-0.35, 0, 0.35].map((r) => (
      <mesh key={r} position={[1.6, -1.6, 0.2]} rotation={[r, 0, -0.75]}>
        <planeGeometry args={[4.2, 1.7]} />
        <meshBasicMaterial color="#FF9A50" transparent opacity={0.045} depthWrite={false} side={2} blending={2} />
      </mesh>
    ))}
    <Sapling position={[0.2, -0.97, 0.9]} />
    <Cassettes position={[0.2, -0.97, 0.45]} rotation={[0, 0.4, 0]} />
    <Kodama position={[0.2, -0.97, -0.5]} rotation={[0, Math.PI / 2, 0]} />
    <BearCarving position={[0.2, -0.97, -0.9]} rotation={[0, 0.9, 0]} />
  </group>
);

const Rug = () => (
  <group position={[0.8, 0, 0.7]}>
    <Cyl p={[0, 0.01, 0]} a={[2.4, 2.4, 0.04, 64]} c="#E8D6BB" r={1} shadow={false} t="rug" tr={[4, 4]} />
    <Cyl p={[0, 0.015, 0]} a={[2.15, 2.15, 0.04, 64]} c={TERRA} r={1} shadow={false} t="rug" tr={[4, 4]} />
    <Cyl p={[0, 0.02, 0]} a={[1.95, 1.95, 0.04, 64]} c="#E8D6BB" r={1} shadow={false} t="rug" tr={[4, 4]} />
    <Cyl p={[0, 0.025, 0]} a={[0.7, 0.7, 0.04, 48]} c={SAGE} r={1} shadow={false} t="rug" tr={[2, 2]} />
    {Array.from({ length: 48 }, (_, i) => {
      const a = (i / 48) * Math.PI * 2;
      return <Box key={i} p={[Math.cos(a) * 2.44, 0.006, Math.sin(a) * 2.44]} rot={[0, -a, 0]} s={[0.1, 0.008, 0.025]} c="#E8D6BB" r={1} shadow={false} />;
    })}
  </group>
);

const Desk = () => (
  <group>
    <Box p={[0.6, 1.0, -3.35]} s={[3.2, 0.08, 1.1]} c={WOOD} t="wood" tr={[3, 1]} r={0.45} round={0.02} />
    {[[-0.9, -3.83], [-0.9, -2.87], [1.3, -3.83], [1.3, -2.87]].map(([x, z]) => (
      <Box key={`${x}${z}`} p={[x + 0.0, 0.48, z]} s={[0.07, 0.96, 0.07]} c={WOOD_DARK} t="wood" />
    ))}
    <Box p={[1.8, 0.5, -3.35]} s={[0.8, 0.92, 1.0]} c="#7A4E2E" t="wood" round={0.02} />
    {[0.25, 0.55, 0.82].map((y) => (
      <Box key={`d${y}`} p={[1.8, y, -2.85]} s={[0.74, 0.26, 0.01]} c="#6E4428" t="wood" r={0.6} />
    ))}
    {[0.25, 0.55, 0.82].map((y) => (
      <Box key={y} p={[1.8, y, -2.84]} s={[0.22, 0.03, 0.03]} c="#D9A441" m={0.7} r={0.3} />
    ))}
    {/* ordinateur */}
    <Box p={[0.55, 1.055, -3.6]} s={[0.72, 0.03, 0.48]} c="#8E9096" m={0.6} r={0.35} t="brushed" round={0.012} />
    <Box p={[0.55, 1.072, -3.65]} s={[0.62, 0.004, 0.24]} c="#2B2A2E" r={0.6} shadow={false} />
    <group position={[0.55, 1.29, -3.83]} rotation={[-0.22, 0, 0]}>
      <Box p={[0, 0, 0]} s={[0.72, 0.46, 0.02]} c="#8E9096" m={0.6} r={0.35} t="brushed" round={0.008} />
      <Box p={[0, 0, 0.012]} s={[0.66, 0.4, 0.005]} c="#1C2A33" e="#3E5C76" ei={0.7} shadow={false} />
      {[["#C85A32", -0.15, 0.25], ["#6E8B74", 0.05, 0.35], ["#E69C24", 0.0, 0.2]].map(([col, x, w], i) => (
        <Box key={i} p={[x, -0.1 - i * 0.05, 0.016]} s={[w, 0.03, 0.003]} c={col} e={col} ei={0.8} shadow={false} />
      ))}
    </group>
    {/* lampe de bureau */}
    <group position={[-0.7, 1.04, -3.6]}>
      <Cyl p={[0, 0.02, 0]} a={[0.13, 0.15, 0.04, 24]} c="#2F3A33" />
      <Box p={[0, 0.3, 0.05]} s={[0.03, 0.6, 0.03]} rot={[0.2, 0, 0]} c="#2F3A33" />
      <mesh position={[0, 0.6, 0.2]} rotation={[0.9, 0, 0]} castShadow>
        <coneGeometry args={[0.14, 0.22, 24, 1, true]} />
        <meshStandardMaterial color={SAGE} side={2} />
      </mesh>
      <Ball p={[0, 0.56, 0.24]} rad={0.05} c="#FFE2B0" e="#FFC266" ei={3} />
      <pointLight position={[0, 0.45, 0.35]} color="#FFB852" intensity={3.2} distance={5} decay={1.6} />
    </group>
    <Cyl p={[-0.3, 1.1, -3.05]} a={[0.06, 0.055, 0.13, 20]} c={TERRA} r={0.35} t="plastic" />
    <Cyl p={[-0.3, 1.162, -3.05]} a={[0.052, 0.052, 0.005, 20]} c="#3A2416" r={0.2} shadow={false} />
    <Succulent position={[2.0, 1.04, -3.7]} />
    <Box p={[-0.15, 1.055, -3.5]} s={[0.36, 0.03, 0.26]} rot={[0, 0.15, 0]} c="#F6EEDC" r={0.9} t="paper" round={0.004} />
    <Box p={[-0.13, 1.08, -3.5]} s={[0.36, 0.02, 0.26]} rot={[0, -0.08, 0]} c="#EFE3CF" r={0.9} t="paper" round={0.004} />
  </group>
);

const Lights = () => {
  const bulbs = useMemo(() => {
    const out = [];
    for (let i = 0; i <= 20; i += 1) {
      const t = i / 20;
      out.push([-3.85 + t * 7.7, 4.55 - Math.sin(t * Math.PI * 3) ** 2 * 0.22, -3.95]);
    }
    for (let i = 1; i <= 14; i += 1) {
      const t = i / 14;
      out.push([-3.95, 4.55 - Math.sin(t * Math.PI * 2) ** 2 * 0.22, -3.85 + t * 6]);
    }
    return out;
  }, []);
  return bulbs.map((b, i) => (
    <mesh key={i} position={b}>
      <sphereGeometry args={[0.045, 10, 10]} />
      <meshStandardMaterial color="#FFE4B5" emissive="#FFC266" emissiveIntensity={2.4} />
    </mesh>
  ));
};

const Posters = () => (
  <group>
    <ArtFrame position={[-0.45, 3.15, -4.0]} w={0.95} h={1.35} art="perfectdays" />
    <ArtFrame position={[-1.42, 3.25, -4.0]} w={0.42} h={0.6} art="wanted" frame="#5C3A21" mat={false} />
    <ArtFrame position={[3.65, 2.5, -4.0]} w={0.38} h={0.53} art="frieren" frame="#EFE3CF" />
    <ArtFrame position={[-4.0, 3.0, -2.75]} rotation={[0, Math.PI / 2, 0]} w={0.8} h={1.15} art="blood" />
    <ArtFrame position={[-4.0, 3.75, 2.3]} rotation={[0, Math.PI / 2, 0]} w={1.3} h={0.75} art="revenant" frame="#EFE3CF" />
    <ArtFrame position={[-4.0, 2.85, 1.17]} rotation={[0, Math.PI / 2, 0]} w={0.38} h={0.53} art="jjk" />
  </group>
);

const Easter = () => (
  <group>
    <group position={[-3.65, 2.645, 2.3]} rotation={[0, Math.PI / 2, 0]}>
      <Totoro position={[0.12, 0, 0.02]} />
      <SootSprites position={[-0.12, 0, 0.1]} />
      <Pothos position={[0.52, 0, 0.05]} length={0.9} />
    </group>
    <Gojo position={[-2.55, 2.03, -3.8]} rotation={[0, 0.3, 0]} />
    <StrawHat position={[2.78, 0.645, 0.38]} rotation={[0.1, 0.6, -0.06]} />
    <FrierenStaff position={[2.55, 0, -3.72]} rotation={[-0.13, 0, 0.06]} />
    <Mimic position={[2.78, 0, -2.5]} rotation={[0, -0.7, 0]} />
    <OilDerrick position={[-3.55, 0, 1.0]} rotation={[0, 0.3, 0]} />
  </group>
);

const WallShelf = () => (
  <group>
    <Box p={[-2.4, 2.0, -3.82]} s={[1.5, 0.06, 0.36]} c={WOOD} t="wood" />
    {[["#3E5C76", 0.3], ["#C85A32", 0.26], ["#E69C24", 0.32], ["#6E8B74", 0.24]].map(([c, h], i) => (
      <Box key={i} p={[-3.0 + i * 0.075, 2.03 + h / 2, -3.82]} s={[0.065, h, 0.24]} c={c} t="fabric" round={0.008} />
    ))}
  </group>
);

const Armchair = () => (
  <group position={[2.85, 0, 0.3]} rotation={[0, -2.2, 0]}>
    <Box p={[0, 0.3, 0]} s={[1.0, 0.36, 0.9]} c={SAGE} r={1} t="fabric" tr={[3, 3]} round={0.06} />
    <Box p={[0, 0.75, -0.38]} s={[1.0, 0.75, 0.2]} c={SAGE} r={1} t="fabric" tr={[3, 3]} round={0.07} />
    <Box p={[0.45, 0.55, 0]} s={[0.14, 0.32, 0.9]} c="#5E7A64" r={1} t="fabric" tr={[3, 3]} round={0.06} />
    <Box p={[-0.45, 0.55, 0]} s={[0.14, 0.32, 0.9]} c="#5E7A64" r={1} t="fabric" tr={[3, 3]} round={0.06} />
    <Box p={[0, 0.53, 0.05]} s={[0.74, 0.12, 0.72]} c="#7E9B83" r={1} t="fabric" tr={[3, 3]} round={0.05} />
    <Box p={[0.1, 0.78, -0.2]} s={[0.42, 0.34, 0.12]} rot={[-0.2, 0, 0.12]} c={TERRA} r={1} t="fabric" tr={[2, 2]} round={0.05} />
    <Box p={[-0.2, 0.62, 0.1]} s={[0.7, 0.03, 0.5]} rot={[0, 0.3, 0]} c="#E3CFB0" r={1} t="rug" tr={[2, 2]} round={0.012} />
    {[[-0.42, -0.36], [0.42, -0.36], [-0.42, 0.36], [0.42, 0.36]].map(([x, z]) => (
      <Cyl key={`${x}${z}`} p={[x, 0.06, z]} a={[0.03, 0.02, 0.12, 8]} c={WOOD_DARK} t="wood" />
    ))}
  </group>
);

const FloorLamp = () => (
  <group position={[3.4, 0, 1.9]}>
    <Cyl p={[0, 0.02, 0]} a={[0.22, 0.24, 0.04, 24]} c="#2F3A33" />
    <Cyl p={[0, 0.9, 0]} a={[0.02, 0.02, 1.8, 8]} c="#2F3A33" />
    <mesh position={[0, 1.9, 0]}>
      <cylinderGeometry args={[0.2, 0.32, 0.36, 28, 1, true]} />
      <meshStandardMaterial color="#F3E2C4" emissive="#FFB852" emissiveIntensity={0.8} side={2} map={getTexture("fabric", [3, 1])} />
    </mesh>
    <pointLight position={[0, 1.75, 0]} color="#FFB060" intensity={4} distance={6} decay={1.5} />
  </group>
);

const Sideboard = () => (
  <group position={[-3.55, 0, -0.6]}>
    <Box p={[0, 0.45, 0]} s={[0.8, 0.7, 2.2]} c="#7A4E2E" t="wood" />
    <Box p={[0.41, 0.45, -0.55]} s={[0.02, 0.6, 1.0]} c="#8A5A35" t="wood" />
    <Box p={[0.41, 0.45, 0.55]} s={[0.02, 0.6, 1.0]} c="#8A5A35" t="wood" />
    <Ball p={[0.43, 0.45, -0.1]} rad={0.025} c="#D9A441" m={0.7} />
    <Ball p={[0.43, 0.45, 0.1]} rad={0.025} c="#D9A441" m={0.7} />
    {[[-0.3, -1.0], [-0.3, 1.0], [0.3, -1.0], [0.3, 1.0]].map(([x, z]) => (
      <Cyl key={`${x}${z}`} p={[x, 0.05, z]} a={[0.03, 0.02, 0.1, 8]} c={WOOD_DARK} t="wood" />
    ))}
  </group>
);

export const Decor = () => (
  <group>
    <Shell />
    <Window />
    <Rug />
    <Desk />
    <Lights />
    <Posters />
    <Easter />
    <WallShelf />
    <Armchair />
    <FloorLamp />
    <Sideboard />
    <Monstera position={[-3.2, 0, 3.55]} scale={1.05} />
    <SnakePlant position={[3.45, 0, -3.45]} scale={1.2} />
  </group>
);
